import { useState, useCallback } from "react";
import {
  Avatar,
  Box,
  IconButton,
  Popover,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Typography,
} from "@mui/material";
import PersonIcon from "@mui/icons-material/Person";
import LogoutIcon from "@mui/icons-material/Logout";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";

export function UserProfileMenu() {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const open = Boolean(anchorEl);

  const handleClick = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  }, []);

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleNavigate = useCallback(
    (path: string) => {
      setAnchorEl(null);
      // Use setTimeout to ensure popover closes before navigation
      setTimeout(() => {
        navigate(path);
      }, 0);
    },
    [navigate]
  );

  const handleSignOut = useCallback(async () => {
    setAnchorEl(null);
    await signOut();
    navigate("/login");
  }, [signOut, navigate]);

  const displayName = user?.display_name || user?.email || "User";
  const initials = displayName
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);

  return (
    <Box>
      <IconButton
        onClick={handleClick}
        size="small"
        aria-controls={open ? "profile-menu" : undefined}
        aria-haspopup="true"
        aria-expanded={open ? "true" : undefined}
        aria-label="Open profile menu"
      >
        <Avatar
          src={user?.photo_url || undefined}
          sx={{
            width: 40,
            height: 40,
            bgcolor: "primary.main",
            fontSize: "0.9rem",
            fontWeight: 600,
          }}
        >
          {initials}
        </Avatar>
      </IconButton>
      <Popover
        id="profile-menu"
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        disableScrollLock
        slotProps={{
          paper: {
            elevation: 3,
            sx: {
              minWidth: 220,
              mt: 1,
              borderRadius: 2,
            },
          },
        }}
      >
        <Box sx={{ px: 2, py: 1.5 }}>
          <Typography variant="subtitle1" fontWeight={600}>
            {displayName}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {user?.email}
          </Typography>
          {user?.job_title && (
            <Typography variant="caption" color="text.secondary">
              {user.job_title}
            </Typography>
          )}
        </Box>
        <Divider />
        <List disablePadding>
          <ListItemButton onClick={() => handleNavigate("/profile")}>
            <ListItemIcon>
              <PersonIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Profile" />
          </ListItemButton>
        </List>
        <Divider />
        <List disablePadding>
          <ListItemButton onClick={handleSignOut}>
            <ListItemIcon>
              <LogoutIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Sign out" />
          </ListItemButton>
        </List>
      </Popover>
    </Box>
  );
}
